import { Injectable } from '@angular/core';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { ApiService, PreferenceChildData, PreferenceData, PreferenceSearchEditResult, UpdatePreferenceResponse } from '@ddt-web/data-service';
import { WebUserDataConstants } from '@ddt-web/domain';
import { ErrorObject } from '@ddt-web/error-handler';
import { StoreEntity } from '@ddt-web/store-service';
import { Store } from '@ngrx/store';
import * as fromPairs from 'lodash/fromPairs';
import * as isEmpty from 'lodash/isEmpty';
import * as sortBy from 'lodash/sortBy';
import * as uniqBy from 'lodash/uniqBy';
import { Observable, of } from 'rxjs';
import { catchError, first, map, switchMap, tap } from 'rxjs/operators';

import { FundPreferenceErrors } from '../models/fund-preference.interface';
import { fundPreferenceChildMissingPerms, fundPreferenceMissingPerms } from '../store/fund-preferences.actions';
import { FundPreferenceStoreService } from './fund-preference-store.service';

/**
 * Service used to handle a fund level features e.g. IQ Fund Tags or IQ Dismissal/Acceptance (Interactions) of Statuses.
 * This service contains the generic logic used for FundTags and Fund Status Interactions which are stored as user preference children.
 * 
 * Each type of fund level feature should extend this service in its own Library and provide its own implementation of the FundPreferenceStoreService 
 * that correctly handles interactions with the respective store for the fund property.
 * 
 * As this is all generic logic, the expectation is that it can be overridden from the fund property lib if necessary.
 */
@Injectable({
	providedIn: 'root'
})
export abstract class FundPreferenceService {
	@InjectConfig(SharedConfigKey.MAXIMUM_FUND_PREFERENCE_CHILDREN_TO_UPDATE) private readonly maximumFundPreferenceChildrenToUpdate = 100;

	protected abstract readonly errorDcrKeys: FundPreferenceErrors;

	constructor(
		protected apiService: ApiService,
		protected fundPreferenceStoreService: FundPreferenceStoreService,
		protected store: Store
	) { }

	public getAllFundPreference<T>(preferenceKey: string, store: string): Observable<T[]> {
		return this.apiService.fetchPreference(preferenceKey).pipe(
			map((response: PreferenceData) => {
				const preferenceId = response?.uniqueId?.split('@')?.[0];
				let children = response?.children ?? [];

				// check if there are fund preference duplicates - this can happen if users adds a preference in 2 separate windows at the same time
				if (uniqBy(children, 'id').length !== children?.length) {
					console.log('Fund preference duplicates found');

					// sanitize children to remove children with same id and outdated timestamp
					children = uniqBy(sortBy(children, ['id', 'updateTimestamp']).reverse(), 'id');

					// identify children ids to remove from preference
					const childrenIds = children.map(children => children.uniqueId);
					const idsToRemove = response.children.map(children => children.uniqueId).filter(childUniqueId => !childrenIds.includes(childUniqueId));

					// remove outdated fund preference duplicates - no extra error handling is needed as the funds will be ignored on the front end regardless
					console.log('Removing duplicates: ' + idsToRemove);
					this.apiService.removePreference(idsToRemove).pipe(first()).subscribe();

					response.children = children;
				}

				const fundPreferences = !response ? [] : this.translatePreferenceChildren(children);

				// check and update parent preference object with wud perms
				if (!response?.perms) {
					this.store.dispatch(fundPreferenceMissingPerms({ payload: { store } }));
				}

				// check and update child preference objects with with wud perms
				const childrenMissingWudPerms: Set<string> = children.reduce((childrenMissingWudPerms, child) => {
					if (!child.perms) {
						childrenMissingWudPerms.add(child.id);
					}
					return childrenMissingWudPerms;
				}, new Set<string>());
				if (childrenMissingWudPerms.size) {
					this.store.dispatch(
						fundPreferenceChildMissingPerms({
							payload: {
								store,
								childrenMissingWudPerms: new Set(Array.from(childrenMissingWudPerms).slice(0, this.maximumFundPreferenceChildrenToUpdate)),
								fundPreferences
							}
						})
					);
				}

				return { preferenceId, fundPreferences };
			}),
			map(({ fundPreferences }) => fundPreferences)
		);
	}

	public editPreferences<T, S extends StoreEntity<T>>(preferencesToEdit: S, preferencesToEditPreviousValue: S, store: string): Observable<T[]> {
		const preferenceEntries = Object.entries(preferencesToEdit);
		if (!preferenceEntries.length) {
			return of([]);
		}

		const preferenceData = preferenceEntries.map(([, preferenceData]: [string, T]) => {
			const childPreference: PreferenceChildData = {
				...preferenceData,
				type: 'PREFERENCE_CHILD',
				perms: WebUserDataConstants.USER_DATA_PERMS
			};
			return childPreference;
		});

		return this.apiService.editPreference(preferenceData, { overwriteArrayNodes: true }).pipe(
			map((result: UpdatePreferenceResponse) => result?.resultMap?.SEARCH_EDIT_RESULTS?.[0]),
			tap((result: PreferenceSearchEditResult) => {
				if (!result?.success) {
					// Update the store first, to revert the change in the UI and then throw an error to the user
					this.fundPreferenceStoreService.setPreferenceData(Object.values(preferencesToEditPreviousValue), store);
					throw new ErrorObject({ preferenceEndpoint: '/edit', fundPreferences: Object.values(preferencesToEdit), ...result }, this.errorDcrKeys.apiFailure);
				}
			}),
			map((result: PreferenceSearchEditResult) => result?.resultList?.[0]),
			map((preference: PreferenceData) => this.translatePreferenceChildren(preference?.children ?? []))
		);
	}

	protected removePreferences<T, S extends StoreEntity<T>>(
		fundIds: string[],
		preferencesToRemovePreviousValue: S,
		store: string,
		shouldIgnoreApiErrors: boolean = false
	): Observable<T[]> {
		if (!fundIds.length) {
			return of([]);
		}

		const uniqueIdsToRemove = fundIds.map((childId: string) => preferencesToRemovePreviousValue[childId]['uniqueId']);

		return this.apiService.removePreference(uniqueIdsToRemove).pipe(
			catchError(error => {
				return shouldIgnoreApiErrors ? of({ resultMap: { SEARCH_EDIT_RESULTS: [{ success: true }] } }) : error;
			}),
			map((result: UpdatePreferenceResponse) => result?.resultMap?.SEARCH_EDIT_RESULTS?.[0]),
			tap((result: PreferenceSearchEditResult) => {
				if (!result?.success) {
					// Update the store first, to revert the change in the UI and then throw an error to the user
					this.fundPreferenceStoreService.setPreferenceData(Object.values(preferencesToRemovePreviousValue), store);
					throw new ErrorObject({ preferenceEndpoint: '/remove', fundIds, ...result }, this.errorDcrKeys.apiFailure);
				}
			}),
			map((result: PreferenceSearchEditResult) => result?.resultList?.[0]),
			map((preference: PreferenceData) => this.translatePreferenceChildren(preference?.children ?? []))
		);
	}

	protected addFundPreferences<T, S extends StoreEntity<T>>(
		preferencesToAdd: S,
		existingPreferences: S,
		preferenceKey: string,
		applicationSuite: string,
		store: string,
		maxPreferenceLimit: number
	): Observable<T[]> {
		if (!Object.entries(preferencesToAdd).length) {
			return of([]);
		}

		const preferenceData: PreferenceData = {
			preferenceKey: preferenceKey,
			type: 'PREFERENCE',
			applications: [applicationSuite.toUpperCase()],
			children: Object.entries(preferencesToAdd).map(([, preferenceData]: [string, T]) => ({
				...preferenceData,
				type: 'PREFERENCE_CHILD',
				perms: WebUserDataConstants.USER_DATA_PERMS
			}))
		};

		return this.apiService.addPreference(preferenceData).pipe(
			map((result: UpdatePreferenceResponse) => result?.resultMap?.SEARCH_EDIT_RESULTS?.[0]),
			tap((result: PreferenceSearchEditResult) => {
				if (!result?.success) {
					// Update the store first, to revert the change in the UI and then throw an error to the user
					this.fundPreferenceStoreService.removeAllById(Object.keys(preferencesToAdd), store);
					if (Object.keys(existingPreferences).length + Object.keys(preferencesToAdd).length > maxPreferenceLimit) {
						// If failed and the number of stored preferences is greater than the maximum possible amount, throw tagged fund limit exceeded error
						throw new ErrorObject(
							{ preferenceEndpoint: '/add', fundPreferences: Object.values(preferencesToAdd), ...result },
							this.errorDcrKeys.fundPreferencePerFundLimitExceeded
						);
					}
					throw new ErrorObject({ preferenceEndpoint: '/add', fundPreferences: Object.values(preferencesToAdd), ...result }, this.errorDcrKeys.apiFailure);
				}
			}),
			map((result: PreferenceSearchEditResult) => result?.resultList?.[0]),
			map((preference: PreferenceData) => this.translatePreferenceChildren(preference?.children ?? []))
		);
	}

	protected setPreferences<T, S extends StoreEntity<T>>(
		preferenceEntries: S,
		preferencesToAdd: S,
		preferencesToRemove: S,
		preferencesToEdit: S,
		store: string,
		preferenceKey: string,
		applicationSuite: string,
		maxPreferenceLimit: number,
		shouldIgnoreApiErrors: boolean = false
	): Observable<T[]> {
		const previousValuePreferencesToEdit: S = fromPairs(Object.entries(preferencesToEdit).map(([clientId]: [string, T]) => [clientId, preferenceEntries[clientId]]));
		const previousValuePreferencesToRemove: S = fromPairs(Object.entries(preferencesToRemove).map(([clientId]: [string, T]) => [clientId, preferenceEntries[clientId]]));
		const fundIdsForPreferencesToRemove: string[] = Object.keys(preferencesToRemove);

		// update preferencesToEdit with unique id
		Object.entries(previousValuePreferencesToEdit).forEach(([clientId, preference]) => {
			preferencesToEdit[clientId]['uniqueId'] = preference['uniqueId'];
		});

		// update store, regardless of action (add/ edit/ remove to ensure we show instant results in the table)
		this.setPreferencesUpdateStores(preferencesToEdit, preferencesToAdd, Object.keys(preferencesToRemove), store);

		// one API call will be made for updating existing preferences and one will be made for adding new preferences (only if applicable)
		return this.addFundPreferences<T, S>(preferencesToAdd, preferenceEntries, preferenceKey, applicationSuite, store, maxPreferenceLimit).pipe(
			tap((addedPreferences: T[]) => {

				const updatedPreferencesToAdd = {};
				Object.keys(preferencesToAdd).forEach(clientId => {
					updatedPreferencesToAdd[clientId] = Object.assign({}, { ...preferencesToAdd[clientId], uniqueId: addedPreferences.find(preference => preference['clientId'] === clientId)['uniqueId'] }); // ['uniqueId'] = ;
				});

				// if any preference added, update the store again with the updated preferencesToAdd with uniqueId (needed if user edits/ removes it)
				if (!isEmpty(updatedPreferencesToAdd)) {
					this.setPreferencesUpdateStores(preferencesToEdit, updatedPreferencesToAdd, Object.keys(preferencesToRemove), store);
				}
			}),
			switchMap((addedPreferences: T[]) =>
				this.editPreferences<T, S>(preferencesToEdit, previousValuePreferencesToEdit, store).pipe(
					switchMap((editedPreferences: T[]) =>
						this.removePreferences<T, S>(
							fundIdsForPreferencesToRemove,
							previousValuePreferencesToRemove,
							store,
							shouldIgnoreApiErrors
						).pipe(map(() => editedPreferences))
					),
					map((editedPreferences: T[]) => [...addedPreferences, ...editedPreferences])
				)
			)
		);
	}

	protected setPreferencesUpdateStores<T, S extends StoreEntity<T>>(preferencesToEdit: S, preferencesToAdd: S, fundIdsForPreferencesToRemove: string[], store: string): void {
		// Update the store first, to make sure we show the new preferences in the UI while the API call is being executed
		this.fundPreferenceStoreService.setPreferenceData([...Object.values(preferencesToEdit), ...Object.values(preferencesToAdd)], store);
		this.fundPreferenceStoreService.removeAllById(fundIdsForPreferencesToRemove, store);
	}

	/**
	 * Convert the preference children to the objects that you want to use in the application
	 * User preference children will come back from the user-preference API with a lot of metadata that is appended upon saving
	 * @param preferenceChildren the full preference children objects returned by the user-preference API
	 */
	protected abstract translatePreferenceChildren(preferenceChildren?: PreferenceChildData[]): any[];
}
